<template>
  <div class="combobox-rental-mutation-base-display">
    <div class="float-right w-fit pl-1 text-right">
      <PBadgeRentalFlowDirection v-if="showBadge" :flow-direction="flowDirection" />
    </div>
    <div class="space-x-1">
      <!-- Counterbooking quantity information for selected item -->
      <div v-if="rentalMutation.counterMutation && !error" class="font-bold sm:text-sm">
        <div
          v-if="showProject"
          :class="
            rentalMutation.counterMutation.project?.id !== instruction?.project?.id
              ? 'text-warning'
              : ''
          "
          :title="
            rentalMutation.counterMutation.project?.id !== instruction?.project?.id
              ? 'Let op: Dit project wijkt af van de instructie'
              : undefined
          "
        >
          {{ rentalMutation.counterMutation.project?.getDisplayText() }}
        </div>
      </div>

      <!-- Quantity information for issuance i.e. selected item or list item -->
      <div
        v-else-if="rentalMutation.flowDirection === RentalMutationFlowDirections.ISSUANCE"
        class="font-bold sm:text-sm"
      >
        <div v-if="showProject">
          <div
            v-if="rentalMutation.project"
            :class="
              rentalMutation.isIssuance() && rentalMutation.project?.id !== instruction?.project?.id
                ? 'text-warning'
                : ''
            "
            :title="
              rentalMutation.isIssuance() && rentalMutation.project?.id !== instruction?.project?.id
                ? 'Let op: Dit project wijkt af van de instructie'
                : undefined
            "
          >
            {{ rentalMutation.project.getDisplayText() }}
          </div>
          <div v-else class="text-error">Geen project geselecteerd!</div>
        </div>
      </div>

      <slot />

      <!-- Counterbooking total information for selected rental mutation issuances -->
      <div
        v-if="
          showCounterBooking &&
          !isListItem &&
          rentalMutation.flowDirection === RentalMutationFlowDirections.ISSUANCE &&
          rentalMutation.getCounterQuantity() > 0
        "
      >
        {{ getCounterBookingText() }}
      </div>

      <div>
        <!-- If the rental mutation is a list item, show the counterbooking total information -->
        <span v-if="showDate && isListItem">
          Uitgifte: {{ rentalMutation.commencementDate?.format("DD-MM-YY") }}
        </span>
        <!-- Or if the commencement date deviates from the instruction date -->
        <span
          v-else-if="
            showDate &&
            rentalMutation.commencementDate &&
            !rentalMutation.commencementDate?.isSame(instruction.date)
          "
        >
          {{
            `${flowDirection === RentalMutationFlowDirections.ISSUANCE ? "Uitgiftedatum: " : "Retourdatum: "}${rentalMutation.commencementDate?.format("DD-MM-YYYY")}`
          }}
        </span>

        <!-- Counterbooking quantity information for selected item -->
        <span v-if="rentalMutation.counterMutation && !error">
          <!-- Only show the separator if there is a date shown and it deviates from the instruction date -->
          <span
            v-if="
              showDate &&
              rentalMutation.commencementDate &&
              (isListItem || !rentalMutation.commencementDate?.isSame(instruction.date)) &&
              !error &&
              showCounterBooking
            "
          >
            |
          </span>
          <ComboboxRentalCounterMutationDisplay
            v-if="showCounterBooking"
            :model-value="rentalMutation.counterMutation"
            :is-being-updated="isBeingUpdated"
          />
        </span>

        <!-- Counterbooking quantity information for list item -->
        <span
          v-else-if="
            isListItem && rentalMutation.flowDirection === RentalMutationFlowDirections.ISSUANCE
          "
        >
          <ComboboxRentalCounterMutationDisplay
            v-if="showCounterBooking"
            :model-value="rentalMutation"
            :is-being-updated="isBeingUpdated"
          />
        </span>
      </div>

      <!-- Error display -->
      <div v-if="error && showError">
        <ComboboxProcessingItemErrorDisplay
          :model-value="error"
          @clear-error="$emit('clearError')"
        />
      </div>

      <div v-if="showDate && !rentalMutation.commencementDate" class="text-error">
        Geen datum geselecteerd!
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import PBadgeRentalFlowDirection from "@/components/administration/PBadgeRentalFlowDirection.vue"
import ComboboxRentalCounterMutationDisplay from "@/components/administration/combobox-display-templates/nested/ComboboxRentalCounterMutationDisplay.vue"
import ComboboxProcessingItemErrorDisplay from "@/components/administration/combobox-display-templates/nested/ComboboxProcessingItemErrorDisplay.vue"
import { computed } from "vue"
import { RentalIntake } from "@/models/rental-intake.model"
import { RentalMutation } from "@/models/rental-mutation.model"
import { RentalDispense } from "@/models/rental-dispense.model"
import { Instruction } from "@/models/instruction.model"
import { BaseException } from "@/exceptions/base-exception.exception"
import { RentalMutationFlowDirections } from "@/enums/rental-mutation-flow-directions"

const props = withDefaults(
  defineProps<{
    item: RentalIntake | RentalDispense | [RentalIntake] | [RentalDispense]
    instruction: Instruction
    flowDirection: RentalMutationFlowDirections
    isListItem?: boolean
    showBadge?: boolean
    showCounterBooking?: boolean
    showDate?: boolean
    showProject?: boolean
    showError?: boolean
    isBeingUpdated: boolean
    error?: BaseException
  }>(),
  {
    showBadge: true,
    showCounterBooking: true,
    showDate: true,
    showProject: true
  }
)

defineEmits(["clearError"])

const rentalMutation = computed(() => {
  if (props.item instanceof RentalMutation) {
    return props.item
  }
  return props.item[0]
})

function getCounterBookingText() {
  const counterQuantity = rentalMutation.value.getCounterQuantity()
  const counteredByCount = rentalMutation.value.counteredBy.filter(
    (rentalMutation) => rentalMutation.quantity > 0
  ).length

  return `${counterQuantity} afgeboekt in ${counteredByCount} tegenmutatie${
    counteredByCount > 1 ? "s" : ""
  }`
}
</script>

<style scoped></style>
